import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Modal,
  Button,
  Combobox,
  DateInput,
  NumberInput,
} from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { AUDIT_COLUMNS, sortByLabel } from '../../../config/columns';
import { incomeService, bankAccountService, incomeTypeService } from '../../../services/resources';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const emptyForm = () => ({
  transaction_date: today(),
  bank_id: '',
  income_id: '',
  amount: '',
  remark: '',
  idempotency_key: '',
});

export default function IncomePage({ onToast }) {
  const { can } = useAuth();
  const canView = can('PENDAPATAN', 'CanView');
  const canCreate = can('PENDAPATAN', 'CanCreate');
  const canEdit = can('PENDAPATAN', 'CanEdit');
  const canDelete = can('PENDAPATAN', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const [banks, setBanks] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      setRows(await incomeService.list(from, to));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const changeDateFrom = (v) => {
    setDateFrom(v);
    load(v, dateTo);
  };
  const changeDateTo = (v) => {
    setDateTo(v);
    load(dateFrom, v);
  };

  useEffect(() => {
    load();
    Promise.all([bankAccountService.list(), incomeTypeService.list()])
      .then(([b, i]) => {
        setBanks(b);
        setIncomeTypes(i);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const bankOptions = sortByLabel(banks
    .filter((b) => !b.is_blocked)
    .map((b) => ({ value: b.id, label: b.bank_name })));
  const incomeOptions = sortByLabel(incomeTypes.map((i) => ({ value: i.id, label: i.name })));

  const FIELD_VALID = {
    transaction_date: (v) => !!v,
    bank_id: (v) => !!v,
    income_id: (v) => !!v,
    amount: (v) => Number(v) > 0,
  };

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const check = FIELD_VALID[k];
      if (check && check(v)) {
        const next = { ...prev };
        delete next[k];
        return next;
      }
      return prev;
    });
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const keys = ['doc_no', 'bank_name', 'income_name', 'remark'];
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), idempotency_key: crypto.randomUUID() });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      transaction_date: String(row.transaction_date).slice(0, 10),
      bank_id: row.bank_id,
      income_id: row.income_id,
      amount: row.amount,
      remark: row.remark || '',
      idempotency_key: row.idempotency_key,
    });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.transaction_date) e.transaction_date = 'Date is required';
    if (!form.bank_id) e.bank_id = 'Bank is required';
    if (!form.income_id) e.income_id = 'Income type is required';
    if (!(Number(form.amount) > 0)) e.amount = 'Must be greater than 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (savingRef.current) return;
    if (!validate()) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    const payload = {
      transaction_date: form.transaction_date,
      bank_id: Number(form.bank_id),
      income_id: Number(form.income_id),
      amount: Number(form.amount),
      remark: form.remark || null,
      idempotency_key: form.idempotency_key,
    };
    try {
      if (editing) {
        await incomeService.update(editing.id, payload);
        onToast?.('Income updated');
      } else {
        await incomeService.create(payload);
        onToast?.('Income saved');
      }
      setOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await incomeService.remove(deleteTarget.id);
      onToast?.('Income deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  if (!canView) {
    return (
      <Card title="Income">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const columns = [
    { key: 'doc_no', label: 'Doc No' },
    { key: 'transaction_date', label: 'Date', render: (r) => String(r.transaction_date).slice(0, 10) },
    { key: 'bank_name', label: 'Bank' },
    { key: 'income_name', label: 'Income Type' },
    { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), className: 'text-right' },
    { key: 'remark', label: 'Remark' },
    ...AUDIT_COLUMNS,
    {
      key: '__a',
      label: 'Actions',
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card title="Income" actions={canCreate ? <Button onClick={openCreate}>+ New</Button> : null}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={dateFrom} onChange={changeDateFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={dateTo} onChange={changeDateTo} />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        loading={loading}
        maxHeight="60vh"
        className="rounded-md border border-gray-100"
      />
      <p className="mt-2 text-xs text-gray-400">{filtered.length} income record(s)</p>

      {/* Create / Edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${editing ? 'Edit' : 'New'} Income${editing ? ` — ${editing.doc_no}` : ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
          <DateInput
            label="Date"
            required
            value={form.transaction_date}
            error={errors.transaction_date}
            onChange={(v) => set('transaction_date', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Combobox
              label="Bank"
              required
              placeholder="-- select bank --"
              options={bankOptions}
              value={form.bank_id}
              error={errors.bank_id}
              onChange={(v) => set('bank_id', v)}
            />
            <Combobox
              label="Income Type"
              required
              placeholder="-- select income type --"
              options={incomeOptions}
              value={form.income_id}
              error={errors.income_id}
              onChange={(v) => set('income_id', v)}
            />
          </div>
          <NumberInput
            label="Amount"
            required
            value={form.amount}
            error={errors.amount}
            onChange={(v) => set('amount', v)}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Remark</span>
            <textarea
              value={form.remark}
              onChange={(e) => set('remark', e.target.value)}
              style={{ resize: 'none' }}
              className="h-16 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Income"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">This will be permanently deleted. Continue?</p>
      </Modal>
    </Card>
  );
}
